import React, { useCallback, useEffect, useState } from 'react';
import { TopBar } from '@/components/TopBar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import { toast } from 'sonner';

export default function NotificationsPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/notifications');
      setItems(r.data || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const readAll = async () => {
    try {
      await api.post('/notifications/read-all');
      load();
    } catch (err) {
      console.error('Failed to mark all read:', err);
      toast.error('Failed to update');
    }
  };

  return (
    <div data-testid="notifications-page">
      <TopBar title="Notifications" subtitle={items.filter((n) => !n.read).length + ' unread'} user={user} right={
        <Button variant="outline" size="sm" onClick={readAll} data-testid="notifications-mark-all-button" className="rounded-xl"><CheckCheck className="h-4 w-4 mr-1" />Mark all read</Button>
      } />
      {loading ? (
        <div className="h-32 rounded-2xl bg-secondary animate-pulse" />
      ) : items.length === 0 ? (
        <EmptyState title="You're all caught up" description="New invites, replies and updates will show here." />
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {items.map((n) => (
            <Card key={n.id} data-testid={`notification-${n.id}`} onClick={() => n.link && navigate(n.link)} className={`p-3 rounded-2xl flex items-start gap-3 cursor-pointer ${!n.read ? 'border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5' : ''}`}>
              <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center"><Bell className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{n.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                <div className="text-[10px] text-muted-foreground/80 mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {!n.read && <span className="h-2 w-2 rounded-full bg-[hsl(var(--accent))] mt-1.5" />}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
